<template>
    <div class="payments-container">
        <!-- Existing Payments Section -->
        <div class="existing-payments-section">
            <div class="section-title">
                <span class="title-text">Payment History</span>
                <span class="title-badge" v-if="invoice.payment_methods.length > 0">{{invoice.payment_methods.length}} payments</span>
            </div>

            <div v-if="invoice.payment_methods.length == 0" class="empty-state">
                <div class="empty-icon">💳</div>
                <div class="empty-text">No payments made yet</div>
            </div>

            <div v-else class="payments-table-wrapper">
                <table class="payments-table">
                    <thead>
                        <tr>
                            <th>Date/Time</th>
                            <th>Method</th>
                            <th class="text-right">Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(ipm, ipmidx) in invoice.payment_methods" :key="ipmidx">
                            <td>{{format_ts(ipm.ts)}}</td>
                            <td>
                                <span class="method-badge" :class="'method-' + (ipm.value || '').toLowerCase()">
                                    {{ (ipm.value || '').toUpperCase() }}
                                </span>
                                <span class="method-detail">{{ ipm.name }}</span>
                            </td>
                            <td class="text-right amount">${{ (parseFloat(ipm.amount) || 0.0).toFixed(2) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Summary Cards -->
            <div class="summary-cards">
                <div class="summary-card total">
                    <div class="card-label">Total</div>
                    <div class="card-value">${{ (invoice.total - total_refunded_value).toFixed(2) }}</div>
                </div>
                
                <div class="summary-card paid">
                    <div class="card-label">Paid</div>
                    <div class="card-value">${{ get_pms_total().toFixed(2) }}</div>
                </div>
                
                <div class="summary-card current">
                    <div class="card-label">Current Payment</div>
                    <div class="card-value">${{ get_current_pms_total().toFixed(2) }}</div>
                </div>
                
                <div class="summary-card balance" :class="{ 'positive': ((invoice.total - total_refunded_value) - get_pms_total() - get_current_pms_total()) > 0 }">
                    <div class="card-label">Remaining Balance</div>
                    <div class="card-value">${{ ( ( (invoice.total - total_refunded_value) - get_pms_total() - get_current_pms_total() ) || 0.0).toFixed(2) }}</div>
                </div>
            </div>

            <!-- Sales Tax Alert -->
            <b-alert 
                v-if="sales_tax > 0" 
                show 
                variant="warning"
                class="tax-alert"
            >
                <div class="alert-content">
                    <span class="alert-icon">💰</span>
                    <span><strong>SALES TAX:</strong> ${{ sales_tax.toFixed(2) }}</span>
                    <span v-if="is_last_payment" class="tax-note">Added to final payment</span>
                </div>
            </b-alert>
        </div>

        <!-- New Payments Section -->
        <div class="new-payments-section">
            <div class="section-header">
                <div class="section-title">
                    <span class="title-text">Add New Payments</span>
                    <span class="title-badge" v-if="payment_methods.length > 0">{{payment_methods.length}} pending</span>
                </div>
                <b-button 
                    @click="add_payment_method" 
                    variant="primary" 
                    size="sm"
                    class="add-payment-btn"
                >
                    <span class="btn-icon">+</span> ADD PAYMENT
                </b-button>
            </div>

            <!-- Payment Cards Grid -->
            <div v-if="payment_methods.length > 0" class="payment-cards-grid">
                <b-card 
                    v-for="(payment_method, pmidx) in payment_methods"
                    :key="pmidx"
                    class="payment-card"
                    :class="{'tax-payment': payment_method.layaway_sls_tax}"
                >
                    <template #header>
                        <div class="card-header-content">
                            <span class="payment-number">Payment #{{pmidx + 1}}</span>
                            <b-badge v-if="payment_method.layaway_sls_tax" variant="dark">SALES TAX</b-badge>
                            <b-button 
                                size="sm" 
                                variant="link" 
                                class="remove-btn"
                                @click="removePaymentMethod(pmidx)"
                            >
                                ×
                            </b-button>
                        </div>
                    </template>

                    <b-card-body>
                        <!-- Payment Method Select -->
                        <div class="form-group">
                            <label class="form-label">Payment Method</label>
                            <b-form-select
                                v-model="payment_methods[pmidx].value"
                                :options="payment_method_options"
                                size="sm"
                                @change="reset_key_metrics"
                                class="form-control"
                            ></b-form-select>
                        </div>

                        <!-- Cash Payment Fields -->
                        <template v-if="payment_methods[pmidx].value == 'cash'">
                            <div class="form-group">
                                <label class="form-label">Amount</label>
                                <b-input 
                                    size="sm" 
                                    type="number" 
                                    v-model="payment_methods[pmidx].amount" 
                                    @keyup="set_validated(false)"
                                    :disabled="payment_method.layaway_sls_tax"
                                    placeholder="Enter amount"
                                    class="form-control"
                                ></b-input>
                            </div>
                        </template>

                        <!-- Credit Card Payment Fields -->
                        <template v-if="payment_methods[pmidx].value == 'cc'">
                            <div class="form-group">
                                <label class="form-label">Amount</label>
                                <b-input 
                                    size="sm" 
                                    type="number" 
                                    v-model="payment_methods[pmidx].amount" 
                                    @keyup="set_validated(false)"
                                    :disabled="payment_method.layaway_sls_tax"
                                    placeholder="Enter amount"
                                    class="form-control"
                                ></b-input>
                            </div>

                            <div class="form-group">
                                <label class="form-label">Last 4 Digits</label>
                                <b-input 
                                    size="sm" 
                                    v-model="payment_methods[pmidx].name" 
                                    @keyup="set_validated(false)"
                                    maxlength="4"
                                    placeholder="****"
                                    class="form-control"
                                ></b-input>
                            </div>
                        </template>

                        <!-- Finance Payment Fields - UPDATED with approved amount -->
                        <template v-if="payment_methods[pmidx].value == 'finance'">
                            <div class="form-group">
                                <label class="form-label">Company</label>
                                <b-form-select
                                    v-model="payment_methods[pmidx].name"
                                    :options="finance_companies"
                                    @change="set_validated(false)"
                                    size="sm"
                                    class="form-control"
                                ></b-form-select>
                            </div>

                            <template v-if="payment_methods[pmidx].name">
                                <!-- Approved Amount Field -->
                                <div class="form-group">
                                    <label class="form-label">Approved Amount</label>
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].approved_amount" 
                                        type="number" 
                                        @keyup="set_validated(false)"
                                        placeholder="Enter approved amount"
                                        class="form-control"
                                    ></b-input>
                                </div>

                                <div class="form-group">
                                    <label class="form-label">Amount to Apply</label>
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].amount" 
                                        type="number" 
                                        @keyup="set_validated(false)"
                                        placeholder="Enter amount to apply"
                                        class="form-control"
                                    ></b-input>
                                    <!-- Warning if applied amount exceeds approved amount -->
                                    <small 
                                        v-if="payment_methods[pmidx].approved_amount && parseFloat(payment_methods[pmidx].amount) > parseFloat(payment_methods[pmidx].approved_amount)"
                                        class="text-danger"
                                    >
                                        Warning: Applied amount exceeds approved amount
                                    </small>
                                </div>

                                <div class="form-group">
                                    <label class="form-label">Lease #</label>
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].lease_id" 
                                        @keyup="set_validated(false)"
                                        placeholder="Enter lease number"
                                        class="form-control"
                                    ></b-input>
                                </div>

                                <div class="form-group">
                                    <label class="form-label">Misc Info</label>
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].misc_info"
                                        placeholder="Additional notes"
                                        class="form-control"
                                    ></b-input>
                                </div>
                            </template>
                        </template>

                        <!-- Store Credit Fields -->
                        <template v-if="payment_methods[pmidx].value == 'store_credit'">
                            <div class="form-group">
                                <label class="form-label">Original Invoice #</label>
                                <div class="input-group">
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].store_credit_invoice_id"
                                        placeholder="Enter invoice ID"
                                        class="form-control"
                                    ></b-input>
                                    <b-button 
                                        size="sm" 
                                        variant="outline-primary"
                                        @click="apply_store_credit(pmidx)"
                                    >
                                        Check
                                    </b-button>
                                </div>
                            </div>

                            <template v-if="payment_methods[pmidx].available_amount">
                                <div class="form-group">
                                    <label class="form-label">Available Credit</label>
                                    <b-input 
                                        size="sm" 
                                        v-model="payment_methods[pmidx].available_amount" 
                                        disabled
                                        class="form-control bg-light"
                                    ></b-input>
                                </div>

                                <div class="form-group">
                                    <label class="form-label">Amount to Apply</label>
                                    <b-input 
                                        size="sm" 
                                        type="number" 
                                        v-model="payment_methods[pmidx].amount" 
                                        @keyup="set_validated(false)"
                                        :max="payment_methods[pmidx].available_amount"
                                        placeholder="Enter amount"
                                        class="form-control"
                                    ></b-input>
                                    <small class="text-muted">Max: ${{payment_methods[pmidx].available_amount}}</small>
                                </div>
                            </template>
                        </template>
                    </b-card-body>
                </b-card>
            </div>

            <!-- Validation Section -->
            <div v-if="payment_methods.length > 0" class="validation-section">
                <div class="validation-controls">
                    <!-- Last Installment Toggle -->
                    <div class="last-installment-group">
                        <label class="form-label">Last Installment?</label>
                        <b-form-radio-group
                            v-model="is_last_payment"
                            :options="last_payment_options"
                            @change="validated = false"
                            class="radio-group"
                        ></b-form-radio-group>
                    </div>

                    <!-- Delivery Date (shown if last installment) -->
                    <div v-if="is_last_payment" class="delivery-date-group">
                        <label class="form-label">Delivery Date</label>
                        <b-form-datepicker 
                            v-model="delivery_date" 
                            required
                            size="sm"
                            class="form-control"
                            placeholder="Select delivery date"
                        ></b-form-datepicker>
                    </div>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                        <b-button 
                            size="md" 
                            variant="warning" 
                            @click="printReceipt"
                            class="validate-btn"
                        >
                            <span class="btn-icon">🖨️</span> PRINT RECEIPT
                        </b-button>
                        <b-button 
                            v-if="!validated"
                            size="md" 
                            variant="warning" 
                            @click="validate"
                            class="validate-btn"
                        >
                            <span class="btn-icon">✓</span> VALIDATE
                        </b-button>
                        <b-button 
                            v-else
                            size="md" 
                            variant="success" 
                            @click="submit_payments"
                            class="submit-btn"
                        >
                            <span class="btn-icon">✓</span> SUBMIT PAYMENTS
                        </b-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import _ from 'underscore'
    import moment from 'moment'
    import global_vars from '@/utilities/global_vars'
    import Backer from '@/components/globals/Backer'
    import Requestor from '@/utilities/Requestor'

    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "LayAwayPaymentsModal",
        components: {Backer,Loading},
        props: ["viewdata", "financecompanies", "total_refunded_value"],
        data: function(){
            return {
                packet: {},
                invoice: {},
                payment_methods: [],
                payment_method_options: [
                    {value: "cc", text: "💳 CREDIT CARD"},
                    {value: "cash", text: "💵 CASH"},
                    {value: "finance", text: "🏦 FINANCE"},
                    {value: "store_credit", text: "🎫 STORE CREDIT"},
                ],
                finance_companies: [],
                is_last_payment: false,
                last_payment_options: [
                    {value: true, text: "YES"},
                    {value: false, text: "NO"}
                ],
                delivery_date: null,
                validated: false,
                sales_tax: 0.0,
            }   
        },
        methods: {
            printReceipt() {
                const printFrame = document.createElement('iframe');
                printFrame.style.position = 'absolute';
                printFrame.style.width = '0';
                printFrame.style.height = '0';
                printFrame.style.border = 'none';
                document.body.appendChild(printFrame);

                const receiptContent = this.generateReceiptHTML();
                
                const frameDoc = printFrame.contentWindow.document;
                frameDoc.open();
                frameDoc.write(receiptContent);
                frameDoc.close();

                printFrame.onload = () => {
                    setTimeout(() => {
                        printFrame.contentWindow.focus();
                        printFrame.contentWindow.print();
                        
                        setTimeout(() => {
                            document.body.removeChild(printFrame);
                        }, 1000);
                    }, 500);
                };
            },

            generateReceiptHTML() {
                let locationName = 'FURNITURE MECCA';
                let locationAddress = '';
                
                if (this.$parent && this.$parent.invoice_location_details) {
                    locationName = this.$parent.invoice_location_details.short_name || locationName;
                    locationAddress = this.$parent.invoice_location_details.address || '';
                }

                const currentDate = new Date().toLocaleString('en-US', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                });

                const invoiceNumber = this.invoice.invoice_id || 'N/A';
                const salesperson = (this.invoice.username || '').toUpperCase();
                const customerName = this.invoice.customer.fullname || 'Walk-in Customer';

                const invoiceTotal = parseFloat(this.invoice.total) || 0;
                const totalPaidSoFar = this.get_pms_total() || 0;
                const currentPaymentTotal = this.get_current_pms_total() || 0;
                const totalPaid = totalPaidSoFar + currentPaymentTotal;
                const balance = invoiceTotal - totalPaid;
                
                const existingPayments = this.invoice.payment_methods || [];
                const currentPayments = this.payment_methods || [];

                return `
                    <!DOCTYPE html>
                    <html>
                    <head>
                        <meta charset="UTF-8">
                        <title>Layaway Payment Receipt - ${invoiceNumber}</title>
                        <style>
                            @page { size: 80mm auto; margin: 0; }
                            body {
                                font-family: 'Courier New', monospace;
                                font-size: 12px;
                                width: 72mm;
                                margin: 0 auto;
                                padding: 2mm;
                                line-height: 1.3;
                            }
                            .header { text-align: center; font-weight: bold; margin-bottom: 3mm; }
                            .store-name { font-size: 16px; font-weight: bold; letter-spacing: 1px; }
                            .store-location { font-size: 12px; margin-top: 1mm; }
                            .receipt-badge { 
                                background: #000; 
                                color: #fff; 
                                padding: 2mm; 
                                text-align: center; 
                                font-weight: bold; 
                                margin: 2mm 0;
                                border-radius: 3px;
                            }
                            .divider { border-top: 1px dashed #000; margin: 2mm 0; }
                            .info-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .info-label { font-weight: bold; }
                            .payment-section { margin: 3mm 0; }
                            .payment-header { 
                                font-weight: bold; 
                                border-bottom: 1px solid #000; 
                                padding-bottom: 1mm; 
                                margin-bottom: 2mm; 
                            }
                            .payment-row { display: flex; justify-content: space-between; margin: 1mm 0; }
                            .payment-detail { font-size: 10px; margin-left: 2mm; color: #666; }
                            .total-row { display: flex; justify-content: space-between; margin: 2mm 0; font-weight: bold; }
                            .grand-total { 
                                font-weight: bold; 
                                font-size: 14px; 
                                border-top: 2px solid #000;
                                border-bottom: 2px solid #000;
                                padding: 2mm 0;
                                margin: 2mm 0;
                            }
                            .balance { 
                                font-weight: bold; 
                                font-size: 14px; 
                                background: #f0f0f0;
                                padding: 2mm;
                                margin: 2mm 0;
                                border-radius: 3px;
                            }
                            .footer { text-align: center; margin-top: 5mm; font-size: 10px; }
                            .thank-you { font-size: 14px; margin: 3mm 0; font-weight: bold; }
                            .cut-line { text-align: center; margin-top: 5mm; font-size: 10px; }
                            .final-payment { 
                                background: #ffe082; 
                                padding: 2mm; 
                                margin: 3mm 0; 
                                text-align: center; 
                                font-weight: bold;
                                border-radius: 3px;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="header">
                            <div class="store-name">${locationName}</div>
                            ${locationAddress ? `<div class="store-location">${locationAddress}</div>` : ''}
                            <div class="store-location">${currentDate}</div>
                        </div>

                        <div class="receipt-badge">LAYAWAY PAYMENT RECEIPT</div>

                        <div class="divider"></div>

                        <div class="info-row">
                            <span class="info-label">Invoice #:</span>
                            <span>${invoiceNumber}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Salesperson:</span>
                            <span>${salesperson}</span>
                        </div>
                        <div class="info-row">
                            <span class="info-label">Customer:</span>
                            <span>${customerName}</span>
                        </div>

                        <div class="divider"></div>

                        <div class="payment-section">
                            <div class="payment-header">INVOICE SUMMARY</div>
                            <div class="payment-row">
                                <span>Original Total:</span>
                                <span>$${invoiceTotal.toFixed(2)}</span>
                            </div>
                            <div class="payment-row">
                                <span>Previously Paid:</span>
                                <span>$${totalPaidSoFar.toFixed(2)}</span>
                            </div>
                        </div>

                        ${existingPayments.length > 0 ? `
                            <div class="payment-section">
                                <div class="payment-header">PREVIOUS PAYMENTS</div>
                                ${existingPayments.map(pm => `
                                    <div class="payment-row">
                                        <span>${this.format_ts(pm.ts)}</span>
                                        <span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span>
                                    </div>
                                    <div class="payment-row" style="font-size: 10px; color: #666;">
                                        <span>${(pm.value || '').toUpperCase()}</span>
                                        <span>${pm.name || ''}</span>
                                    </div>
                                `).join('')}
                            </div>
                        ` : ''}

                        ${currentPayments.length > 0 ? `
                            <div class="payment-section">
                                <div class="payment-header">CURRENT PAYMENT${currentPayments.length > 1 ? 'S' : ''}</div>
                                ${currentPayments.map(pm => `
                                    <div class="payment-row">
                                        <span>${(pm.value || '').toUpperCase()}${pm.layaway_sls_tax ? ' (TAX)' : ''}</span>
                                        <span>$${(parseFloat(pm.amount) || 0).toFixed(2)}</span>
                                    </div>
                                    ${pm.value === 'finance' && pm.approved_amount ? `
                                        <div class="payment-row" style="font-size: 10px; color: #666;">
                                            <span>Approved: $${(parseFloat(pm.approved_amount) || 0).toFixed(2)}</span>
                                            ${pm.lease_id ? `<span>Lease: ${pm.lease_id}</span>` : ''}
                                        </div>
                                    ` : ''}
                                `).join('')}
                                <div class="payment-row" style="margin-top: 2mm; font-weight: bold;">
                                    <span>Current Total:</span>
                                    <span>$${currentPaymentTotal.toFixed(2)}</span>
                                </div>
                            </div>
                        ` : ''}

                        <div class="divider"></div>

                        <div class="grand-total total-row">
                            <span>TOTAL PAID TO DATE:</span>
                            <span>$${totalPaid.toFixed(2)}</span>
                        </div>

                        <div class="balance total-row">
                            <span>REMAINING BALANCE:</span>
                            <span>$${balance.toFixed(2)}</span>
                        </div>

                        ${this.is_last_payment ? `
                            <div class="final-payment">
                                ⚠️ FINAL PAYMENT - INVOICE PAID IN FULL
                                ${this.delivery_date ? `<br>Delivery Date: ${this.delivery_date}` : ''}
                            </div>
                        ` : ''}

                        <div class="footer">
                            <div class="thank-you">THANK YOU FOR YOUR PAYMENT!</div>
                            <div>${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
                        </div>

                        <div class="cut-line">- - - - - - - - - - - - - - - - - - - -</div>
                    </body>
                    </html>
                `;
            },
            apply_store_credit: function(pmidx){
                let payment_methods = this.payment_methods;
                let store_credit_invoice_id = payment_methods[pmidx].store_credit_invoice_id;

                if (!store_credit_invoice_id){
                    this.$vex.dialog.alert(
                        {message: "Please specify valid invoice id to find out its store credit balance."}
                    );
                    return
                }
                let path = "/api/sales/get_available_credit/";
                let data = {invoice_id: store_credit_invoice_id};
                let that = this;

                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );
                    }
                    else {
                        if (response.data.result.length > 0){
                            that.payment_methods[pmidx].available_amount = response.data.result[0].available_amount;
                            that.payment_methods[pmidx].name = store_credit_invoice_id;
                            

                            if(response.data.result[0].available_amount > 0){
                                that.$bvToast.toast(
                                    "Store credit retrieved.", 
                                    {title: "Success", autoHideDelay: 1500, variant:"success"}
                                );
                            }
                            else {
                                that.$bvToast.toast(
                                    "No Store credit available for "+store_credit_invoice_id, 
                                    {title: "Notice", autoHideDelay: 1500, variant:"warning"}
                                );
                            }
                        }
                        else {
                            that.$bvToast.toast(
                                "No store credit found with the given invoice.",
                                {title: "Not found", autoHideDelay: 1500, variant:"warning"}
                            );
                        }   
                    }
                }

                let f = function(error) {
                    console.log(error)
                    that.$bvToast.toast(
                        "Error occurred while applying store credit.", 
                        {title: "Error", autoHideDelay: 1500, variant:"warning"}
                    );
                }
                Requestor.postAsyncP(path, data, this, s, f);
            },
            reset_key_metrics: function(){
                this.is_last_payment=false; this.sales_tax = 0.0; this.validated =  false;
            },
            format_ts: function(ts){
                return moment(ts).format("MM/DD/YYYY hh:mm A")
            },
            round_right: function(value){
                return parseFloat(parseFloat(value).toFixed(2));
            },
            set_validated(value){
                this.validated = value;
            },
           get_current_pms_total: function(){
    let pms = this.payment_methods;
    let total_paid = 0.0;
    for (var i=0; i<pms.length; i++){
        // Skip sales tax payment methods when calculating current payment total
        if (pms[i].layaway_sls_tax) {
            continue;
        }
        let amount = (parseFloat(pms[i].amount) || 0.0);
        total_paid += amount
    }

    // Don't subtract sales tax here since we're excluding it above
    return total_paid;
},
            get_pms_total: function(){
                let pms = this.invoice.payment_methods;
                let total_paid = 0.0;
                for (var i=0; i<pms.length; i++){
                    let amount = (parseFloat(pms[i].amount) || 0.0);
                    total_paid += amount
                }
                return total_paid;
            },
            add_payment_method: function(){
                let idx = 0;
                if (this.payment_methods.length>0){
                    idx = this.payment_methods[this.payment_methods.length-1].idx+1;
                }
                else {
                    if (this.invoice.payment_methods.length > 0){
                        this.invoice.payment_methods.sort((a,b) => a.idx - b.idx);
                        idx = this.invoice.payment_methods[this.invoice.payment_methods.length-1].idx+1
                    }
                }
                
                let new_payment_method = {
                    idx: idx,
                    value: null,
                    amount: null,
                    approved_amount: null, // Added approved_amount field
                    properties: {},
                    name: null,
                    available_amount: 0.0,
                    store_credit_invoice_id: null,
                    charged_tax_itself: false,
                    lease_id: null, // Added lease_id for finance
                    misc_info: null, // Added misc_info for finance
                    new: true,
                }
                this.payment_methods.push(new_payment_method);
                this.validated = false;
                this.sales_tax = 0.0;
                this.is_last_payment = false;
            },
            removePaymentMethod: function(pmidx){
                this.payment_methods.splice(pmidx, 1);
                this.validated = false;
                this.sales_tax = 0.0;
                this.is_last_payment = false;
            },
            validate_payment_methods: function(payment_methods){
                let errors = [];
                let paid_so_far = this.roundTo((this.get_pms_total() + this.get_current_pms_total()  || 0.0), 2);

                for (var i=0; i<payment_methods.length; i++) {

                    if (!payment_methods[i].value || !payment_methods[i].amount || !parseFloat(payment_methods[i].amount) || parseFloat(payment_methods[i].amount) <= 0 ) {
                        errors.push("One or more invalid payment amounts is present. Please indicate payment method and a proper numerical amount.")
                    }

                    if (payment_methods[i].value == "cash"){
                        payment_methods[i].name = "N/A"
                    }

                    if (payment_methods[i].value == "cc"){
                        if (!(payment_methods[i].name)){
                            errors.push("All credit card payments should include their last 4 digits for identity.")
                        }

                        if (!(parseInt(payment_methods[i].name))){
                            errors.push("All credit card payments should include their valid last 4 digits.")
                        }

                        if ((payment_methods[i].name || "").replaceAll(/\s/g, "").length < 4){
                            errors.push("All credit card payments should include their valid last 4 digits without spaces.")
                        }
                    }
                    
                    if (payment_methods[i].value == "finance"){
                        if (!(payment_methods[i].lease_id)){
                            errors.push("All finance related sales should include their lease/finance id.")
                        }
                        if (!(payment_methods[i].name)){
                            errors.push("All finance related sales should include their lease/finance company name.")
                        }
                        
                        // Validate approved amount if provided
                        if (payment_methods[i].approved_amount && parseFloat(payment_methods[i].approved_amount) < 0) {
                            errors.push("Approved amount cannot be negative.")
                        }
                        
                        // Validate that applied amount doesn't exceed approved amount
                        if (payment_methods[i].approved_amount && 
                            parseFloat(payment_methods[i].amount) > parseFloat(payment_methods[i].approved_amount)) {
                            errors.push("Applied amount cannot exceed approved amount for finance payments.")
                        }
                    }

                    if (payment_methods[i].value == "store_credit"){
                        if (parseFloat(payment_methods[i].amount) > parseFloat(payment_methods[i].available_amount) ) {
                            errors.push("All the store credit amounts should be less or equal to available credit amount.")
                        }

                        if (isNaN(parseFloat(payment_methods[i].available_amount)) ||parseFloat(payment_methods[i].available_amount) == 0.0){
                            errors.push("No store credit can be applied with given information.")
                        }
                    }

                    let amount = parseFloat(payment_methods[i].amount);
                    if (!amount){
                        errors.push("Please provide valid amounts with all the payment methods.")
                    }
                    
                }

                if (paid_so_far > this.invoice.total){
                    errors.push("Total payments cannot exceed the existing balance.")
                }

                return errors
            },
            validate: function(){
                let errors = this.validate_payment_methods(this.payment_methods);
                if (errors.length>0){
                    this.$vex.dialog.alert({
                        message: errors[0]
                    });
                    return
                }

                else {
                    let paid_so_far = this.get_current_pms_total() + this.get_pms_total();
                    let invoice_total = this.invoice.total - this.total_refunded_value;

                    if (this.is_last_payment){
                        let sales_tax = this.get_total_tax();
                        if (paid_so_far != invoice_total){
                            this.$vex.dialog.alert({
                                message: "LAST INSTALLMENT SHOULD SET THE BALANCE TO 0."
                            });
                            return
                        }
                        else{
                            this.sales_tax = Number(sales_tax) || 0;
                            this.payment_methods[0].amount = this.roundTo(parseFloat(this.payment_methods[0].amount), 2);

                            let idx = 0;
                            if (this.payment_methods.length>0){
                                idx = this.payment_methods[this.payment_methods.length-1].idx+1;
                            }
                            else {
                                if (this.invoice.payment_methods.length > 0){
                                    this.invoice.payment_methods.sort((a,b) => a.idx - b.idx);
                                    idx = this.invoice.payment_methods[this.invoice.payment_methods.length-1].idx+1
                                }
                            }

                            if (this.sales_tax > 0.0 ){
                                if (this.payment_methods.filter(function(d){return d.layaway_sls_tax == true}).length == 0) {
                                    this.payment_methods.push({
                                        layaway_sls_tax: true,
                                        idx: idx,
                                        value: 'cash',
                                        amount: this.roundTo(parseFloat(this.sales_tax), 2),
                                        properties: {},
                                        name: null,
                                        available_amount: 0.0,
                                        store_credit_invoice_id: null,
                                        charged_tax_itself: false,
                                        new: true,
                                    })
                                }
                            }
                            this.validated = true
                        }
                    }
                    else{
                        if (paid_so_far == invoice_total){
                            this.$vex.dialog.alert({
                                message: "LOOKS LIKE THIS IS THE LAST INSTALLMENT. PLEASE INDICATE IT BELOW."
                            });
                            this.validated = false;
                        }
                        else {
                            this.validated = true
                        }
                    }
                }
            },
            roundTo: function(n, digits) {
                var negative = false;
                if (digits === undefined) {
                    digits = 0;
                }
                if (n < 0) {
                    negative = true;
                    n = n * -1;
                }
                var multiplicator = Math.pow(10, digits);
                n = parseFloat((n * multiplicator).toFixed(11));
                n = (Math.round(n) / multiplicator).toFixed(digits);
                if (negative) {
                    n = (n * -1).toFixed(digits);
                }
                return n;
            },
            get_total_tax: function(){
                let old_payment_methods = this.invoice.payment_methods;
                let payment_methods = this.payment_methods;
                let sales_tax = 0.0;
                for (var i=0; i<old_payment_methods.length; i++){
                    let opm = old_payment_methods[i];
                    if (opm.layaway_sls_tax == true){
                        continue
                    }

                    if (opm.value == "finance"){
                        let opm_details = this.finance_companies.filter(function(d){return opm.name == d.value})[0];

                        if ( !(opm_details.company_charges_tax_in_states.includes(this.invoice.sold_from_state)) ){
                            sales_tax += ((parseFloat(opm.amount) || 0.0) * this.invoice.tax_perc);
                        }
                        else {
                            this.invoice.payment_methods[i].charged_tax_itself = true;
                        }
                    }
                    else {
                        if (["store_credit", "cc", "cash"].includes(opm.value)){
                            sales_tax += (parseFloat(opm.amount) || 0.0) * this.invoice.tax_perc;
                        }
                    }
                }

                for (var i=0; i<payment_methods.length; i++){
                    let pm = payment_methods[i];

                    if (pm.layaway_sls_tax == true){
                        continue
                    }

                    if (pm.value == "finance"){
                        let pm_details = this.finance_companies.filter(function(d){return pm.name == d.value})[0];

                        if ( !(pm_details.company_charges_tax_in_states.includes(this.invoice.sold_from_state)) ){
                            sales_tax += (parseFloat(pm.amount) || 0.0) * this.invoice.tax_perc;
                        }
                        else {
                            this.payment_methods[i].charged_tax_itself = true;
                        }
                    }
                    else {
                        if (["store_credit", "cc", "cash"].includes(pm.value)){
                            sales_tax += (parseFloat(pm.amount) || 0.0) * this.invoice.tax_perc;
                        }
                    }
                }

                return (this.roundTo(sales_tax, 2) || 0.0);

            },

            submit_payments: function(){
                let payment_methods = this.payment_methods;
                let old_payment_methods = JSON.parse(JSON.stringify(this.invoice.payment_methods));
                let final_payment_methods = old_payment_methods.concat(payment_methods); 

                let that = this;
                let data = {
                    invoice_id: this.invoice.invoice_id, 
                    delivery_date: this.delivery_date, 
                    sales_tax: this.sales_tax, 
                    is_last_payment: this.is_last_payment, 
                    payment_methods: final_payment_methods
                }

                if (this.is_last_payment){
                    if (this.validated){
                        if (this.sales_tax > 0){
                            let sales_tax_payment_method = this.payment_methods.filter(function(d){return d.layaway_sls_tax == true});
                            if (sales_tax_payment_method.length > 0){
                                sales_tax_payment_method = sales_tax_payment_method[0];
                                if (sales_tax_payment_method.value == 'cc'){
                                    if ((sales_tax_payment_method.name || "").replaceAll(/\s/g, "").length < 4){
                                            this.$vex.dialog.alert({
                                                message: "PLEASE PROVIDE 4 DIGITS OF CC."
                                            });
                                            return
                                    }
                                }
                            }
                            else {
                                this.$vex.dialog.alert({
                                    message: "NO PAYMENT METHOD SPECIFIED FOR SALES TAX."
                                });
                                return
                            }
                        }
                    }
                    else {
                        this.$vex.dialog.alert({
                            message: "ERROR IN PAYMENT METHODS. PLEASE TRY AGAIN."
                        });
                        return
                    }
                }

                let path = "/api/sales/add_layaway_payments/"
                let s = function(response){
                    if (response.data.error){
                        that.$bvToast.toast(
                            response.data.error, 
                            {title: "Error", autoHideDelay: 1500, variant:"warning"}
                        );

                    }
                    else {
                        that.$bvToast.toast(
                            "ADDED PAYMENTS.", 
                            {title: "Success", autoHideDelay: 1500, variant:"success"}
                        );
                        that.$emit("added-layaway-payments", that.items);
                    }
                }
                let f = function(error){
                    that.$bvToast.toast(
                        "Error updating.",
                        {title: "Error", autoHideDelay: 1500, variant:"danger"}
                    );
                }
                Requestor.postAsyncP(path, data, this, s, f);
                
                return
            }
        },
        created: function(){
            this.packet = JSON.parse(localStorage.packet);
            this.packet.userDetails = JSON.parse(localStorage.userDetails);
            this.invoice = JSON.parse(JSON.stringify(this.viewdata));
            this.total_refunded_value = JSON.parse(JSON.stringify(this.total_refunded_value));
            this.finance_companies = JSON.parse(JSON.stringify(this.financecompanies));
        }
    }
</script>

<style scoped>
/* Container */
.payments-container {
    width: 100%;
    padding: 24px;
    min-height: 350px;
    background: #f8f9fa;
    border-radius: 12px;
}

/* Header */
.invoice-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 16px 24px;
    border-radius: 12px;
    margin-bottom: 24px;
    color: white;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.header-title {
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.print-btn {
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
}

.print-btn:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-1px);
}

/* Section Styles */
.existing-payments-section,
.new-payments-section {
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.title-text {
    font-size: 18px;
    font-weight: 600;
    color: #2d3748;
}

.title-badge {
    background: #e2e8f0;
    color: #4a5568;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px;
    background: #f7fafc;
    border-radius: 8px;
    border: 2px dashed #cbd5e0;
}

.empty-icon {
    font-size: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
}

.empty-text {
    color: #718096;
    font-size: 16px;
}

/* Payments Table */
.payments-table-wrapper {
    overflow-x: auto;
    margin-bottom: 24px;
}

.payments-table {
    width: 100%;
    border-collapse: collapse;
}

.payments-table th {
    background: #f7fafc;
    padding: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #4a5568;
    text-align: left;
    border-bottom: 2px solid #e2e8f0;
}

.payments-table td {
    padding: 12px;
    font-size: 14px;
    color: #2d3748;
    border-bottom: 1px solid #e2e8f0;
}

.method-badge {
    display: inline-block;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    margin-right: 8px;
}

.method-cc { background: #4299e1; color: white; }
.method-cash { background: #48bb78; color: white; }
.method-finance { background: #9f7aea; color: white; }
.method-store_credit { background: #ed8936; color: white; }

.method-detail {
    color: #718096;
    font-size: 12px;
}

.text-right {
    text-align: right;
}

.amount {
    font-weight: 600;
    color: #2d3748;
}

/* Summary Cards */
.summary-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 6px;
    margin-top: 20px;
}

.summary-card {
    background: #f7fafc;
    padding: 6px;
    border-radius: 10px;
    border-left: 4px solid;
}

.summary-card.total { border-left-color: #667eea; }
.summary-card.paid { border-left-color: #48bb78; }
.summary-card.current { border-left-color: #fbbf24; }
.summary-card.balance { border-left-color: #f56565; }
.summary-card.balance.positive { border-left-color: #48bb78; }

.card-label {
    font-size: 14px;
    color: #718096;
    margin-bottom: 8px;
}

.card-value {
    font-size: 14px;
    font-weight: 600;
    color: #2d3748;
}

/* Tax Alert */
.tax-alert {
    margin-top: 20px;
    border: none;
    border-radius: 8px;
    background: #feebc8;
    border-left: 4px solid #ed8936;
}

.alert-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.alert-icon {
    font-size: 20px;
}

.tax-note {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
}

/* Section Header */
.section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
}

.add-payment-btn {
    background: #667eea;
    border: none;
    padding: 8px 16px;
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.add-payment-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(102, 126, 234, 0.25);
}

.btn-icon {
    margin-right: 6px;
}

/* Payment Cards Grid */
.payment-cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
}

.payment-card {
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
}

.payment-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 12px rgba(0, 0, 0, 0.1);
}

.payment-card.tax-payment {
    border: 2px solid #ed8936;
    background: #fffaf0;
}

.card-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f7fafc;
    padding: 12px;
    border-bottom: 1px solid #e2e8f0;
}

.payment-number {
    font-weight: 600;
    color: #4a5568;
}

.remove-btn {
    color: #f56565;
    font-size: 20px;
    padding: 0;
    line-height: 1;
    text-decoration: none;
}

.remove-btn:hover {
    color: #c53030;
}

/* Form Elements */
.form-group {
    margin-bottom: 16px;
}

.form-label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 6px;
}

.form-control {
    width: 100%;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 0px 12px;
    font-size: 14px;
    transition: all 0.3s ease;
}

.form-control:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-group {
    display: flex;
    gap: 8px;
}

.input-group .form-control {
    flex: 1;
}

.input-group .btn {
    white-space: nowrap;
}

.bg-light {
    background: #f7fafc;
}

.text-danger {
    color: #f56565;
    font-size: 12px;
    margin-top: 4px;
    display: block;
}

/* Validation Section */
.validation-section {
    margin-top: 24px;
    padding: 20px;
    background: #f7fafc;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
}

.validation-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 24px;
}

.last-installment-group {
    flex: 1;
    min-width: 100px;
    max-width: 120px;
}

.delivery-date-group {
    flex: 1;
    min-width: 100px;
    max-width: 220px;
}

.radio-group {
    display: flex;
    gap: 16px;
    margin-top: 8px;
}

.action-buttons {
    flex: 1;
    text-align: right;
}

.validate-btn,
.submit-btn {
    padding: 10px 24px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.validate-btn {
    background: #fbbf24;
    color: #744210;
}

.validate-btn:hover {
    background: #f59e0b;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(251, 191, 36, 0.25);
}

.submit-btn {
    background: #48bb78;
    color: white;
}

.submit-btn:hover {
    background: #38a169;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(72, 187, 120, 0.25);
}

/* Responsive Design */
@media (max-width: 768px) {
    .payments-container {
        padding: 16px;
    }
    
    .invoice-header {
        flex-direction: column;
        gap: 12px;
        text-align: center;
    }
    
    .summary-cards {
        grid-template-columns: 1fr;
    }
    
    .validation-controls {
        flex-direction: column;
        align-items: stretch;
    }
    
    .action-buttons {
        text-align: center;
    }
    
    .payment-cards-grid {
        grid-template-columns: 1fr;
    }
}

/* Animation */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.payment-card {
    animation: slideIn 0.3s ease;
}

/* Print Styles */
@media print {
    .hidden-print {
        display: none !important;
    }
    
    .payments-container {
        background: white;
        padding: 0;
    }
    
    .invoice-header,
    .section-header,
    .add-payment-btn,
    .validation-section,
    .print-btn {
        display: none !important;
    }
}

/* Utility Classes */
.text-muted {
    color: #718096;
    font-size: 12px;
    margin-top: 4px;
    display: block;
}
</style>


// WEBPACK FOOTER //
// src/components/Sales/LayAwayPaymentsModal.vue